import { navigate } from "../router.js";
import { useEffect, useRef, useState } from "react";
import { unitById, type Role } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { Timer } from "../ui/Timer.js";
import { Sheet, Stat } from "../base/panels/shared.js";
import { unitsApi } from "./api.js";
import { useUnits } from "./state.js";
import { UnitViewer } from "./UnitViewer.js";
import { StarCeremony } from "./StarCeremony.js";
import { UnitXpPanel } from "./UnitXpPanel.js";
import { UnitEquipment } from "./UnitEquipment.js";
import { useReducedMotion } from "./useReducedMotion.js";
import { ChassisIcon, RoleIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import {
  MfrBadge,
  Stars,
  TierBadge,
  abilityText,
  chassisName,
  roleName,
  tierColor,
  unitName,
} from "./shared.js";
import type { ModuleSlot, UnitView } from "./types.js";
import { ModuleArtwork } from "./ModuleInventory.js";
import { EQUIPMENT_SLOTS } from "./moduleInventoryModel.js";
import { autoEquipOffer } from "./unitSheetModel.js";
import "./unit-sheet.css";


function Viewer({ unitId, accent, skin }: { unitId: string; accent: string; skin: string | null }) {
  const t = useT();
  const [modelError, setModelError] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const reducedMotion = useReducedMotion();
  const ref = useRef<HTMLCanvasElement | null>(null);
  const viewer = useRef<UnitViewer | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    let v: UnitViewer;
    try {
      v = new UnitViewer(ref.current, { accent, reducedMotion });
    } catch {
      setModelError(true);
      return;
    }
    viewer.current = v;
    const ro = new ResizeObserver(() => v.resize());
    ro.observe(ref.current);
    const visibility = new IntersectionObserver(entries => {
      const visible = entries.some(entry => entry.isIntersecting);
      v.setActive(visible);
    });
    visibility.observe(ref.current);
    return () => {
      visibility.disconnect();
      ro.disconnect();
      v.dispose();
      viewer.current = null;
    };
  }, []);
  useEffect(() => {
    if (!viewer.current) return;
    viewer.current?.setAccent(accent);
    let cancelled = false;
    setModelError(false);
    setModelReady(false);
    void viewer.current?.show(unitId).then(() => {
      if (!cancelled) setModelReady(true);
    }).catch(() => {
      if (!cancelled) setModelError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [unitId, accent]);
  useEffect(() => {
    viewer.current?.setReducedMotion(reducedMotion);
  }, [reducedMotion]);
  useEffect(() => viewer.current?.setSkin(skin), [skin]);
  return (
    <div className="unit-viewer" data-testid="unit-viewer" data-model-ready={modelReady}>
      <canvas ref={ref} />
      {modelError ? <p role="alert">{t("units.viewer.error")}</p> : null}
      <div className="viewer-actions" role="group" aria-label={t("units.viewer.actions")}>
        {(["idle", "walk", "attack", "hit", "death"] as const).map((clip) => (
          <button
            key={clip}
            type="button"
            className="chip"
            onClick={() => viewer.current?.play(clip)}
            data-testid={`unit-animation-${clip}`}
            disabled={!modelReady}
          >
            {t(`units.viewer.${clip}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Unit sheet (D69): hero with viewer, power and level, five slot tiles, one action bar, XP, star-up and abilities (R21–R23, R26). */
export function UnitDetail({ rowId }: { rowId: string }) {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const run = useUnits((s) => s.run);
  const select = useUnits((s) => s.select);
  const autoEquip = useUnits((s) => s.autoEquip);
  const inventory = useSession((s) => s.inventory);
  const [section, setSection] = useState<"overview" | "equipment">("overview");
  const [slot, setSlot] = useState<ModuleSlot>("weapon");
  useEffect(() => {
    setSection("overview");
    setSlot("weapon");
  }, [rowId]);
  const [starPending, setStarPending] = useState(false);
  const [ceremony, setCeremony] = useState<UnitView | null>(null);
  const starRequest = useRef(false);
  const alive = useRef(true);
  const activeRow = useRef(rowId);
  activeRow.current = rowId;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const unit = roster?.units.find((u) => u.id === rowId);
  if (!roster || !unit) return null;
  const def = unitById(unit.unitId);
  const color = tierColor(def.tier);
  const cdc = inventory.find((i) => i.itemId === "itm.cdc")?.qty ?? 0;
  const offer = autoEquipOffer(unit, roster.modules);
  const needsRepair = Boolean(unit.repair || unit.wrecked || unit.hpPct < 100);
  const raiseStar = async () => {
    if (starRequest.current || !unit.nextStar) return;
    starRequest.current = true;
    setStarPending(true);
    try {
      const updated = await run(() => unitsApi.star(unit.id, unit.nextStar!.have < unit.nextStar!.cost));
      if (!updated || updated.id !== unit.id || updated.stars <= unit.stars) return;
      // The mutation response remains authoritative even if the follow-up roster fetch fails.
      useUnits.setState((state) => ({
        roster: state.roster
          ? {
              ...state.roster,
              units: state.roster.units.map((row) => (row.id === updated.id ? updated : row)),
            }
          : null,
      }));
      if (alive.current && activeRow.current === updated.id) setCeremony(updated);
    } finally {
      starRequest.current = false;
      if (alive.current) setStarPending(false);
    }
  };
  const openSlot = (value: ModuleSlot) => {
    setSlot(value);
    setSection("equipment");
  };

  /** One action bar as the sheet footer: it never covers the tiles or the viewer (D69 review finding). */
  const actionBar = (
    <div className="unit-actions" data-testid="unit-actions">
      {unit.nextStar ? (
        <Button
          variant="gold"
          loading={starPending}
          aria-busy={starPending}
          disabled={unit.nextStar.have + unit.nextStar.ufr < unit.nextStar.cost}
          onClick={() => void raiseStar()}
          data-testid="unit-star-up"
        >
          <StatIcon stat="star" /> {t("units.star.up")}
        </Button>
      ) : null}
      {needsRepair ? (
        <Button onClick={() => { select(null); navigate("/app/units/repair"); }} data-testid="unit-repair">
          <Icon name="repair" /> {t("battle.repair.heading")}
        </Button>
      ) : null}
      <Button aria-pressed={section === "equipment"} onClick={() => setSection(section === "equipment" ? "overview" : "equipment")} data-testid="unit-tab-equipment">
        {t("gear.equipment")}
      </Button>
      <Button variant={offer.candidates > 0 ? "primary" : "default"} disabled={offer.candidates === 0} onClick={() => void autoEquip(unit.id)} data-testid="unit-auto-equip">
        {t("units.autoEquip")}{offer.candidates > 0 ? ` (${offer.candidates})` : ""}
      </Button>
    </div>
  );

  return (
    <Sheet
      title={
        <span className="row" style={{ gap: 8 }}>
          <TierBadge tier={def.tier} />
          <span style={{ color }}>{unitName(unit.unitId)}</span>
          <span data-testid="unit-authoritative-stars" data-stars={unit.stars}>
            <Stars stars={unit.stars} />
          </span>
        </span>
      }
      onClose={() => select(null)}
      testId="unit-detail"
      footer={actionBar}
      wide
    >
      <div className="unit-sheet">
        <div className="detail-grid unit-hero">
          <Viewer unitId={unit.unitId} accent={color} skin={unit.skin} />
          <div className="unit-hero-copy">
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <span className="badge">
                <RoleIcon role={def.role} /> {roleName(def.role)}
              </span>
              <span className="badge">
                <ChassisIcon chassis={def.chassis} /> {chassisName(def.chassis)}
              </span>
              <MfrBadge mfr={def.mfr} />
              {unit.squad !== null ? <span className="badge ok">{t("units.inSquad", { n: unit.squad })}</span> : null}
              {unit.chemistryPct > 0 ? (
                <span className="badge ok">
                  +{unit.chemistryPct}% {t("units.chemistry")}
                </span>
              ) : null}
            </div>
            <div className="unit-keyfacts">
              <span className="badge gold" data-testid="unit-power">
                <StatIcon stat="power" /> {formatCompact(unit.power)}
              </span>
              <span className="badge unit-level" data-testid="unit-level">
                {t("units.level")} {unit.level}/{unit.levelCap}
              </span>
              <span className="badge">{unit.wrecked ? <><Icon name="warning" /> {t("units.wrecked")}</> : `HP ${unit.hpPct}%`}</span>
              {unit.repair ? <span className="badge"><Icon name="repair" /><Timer finishesAt={unit.repair.finishesAt} /></span> : null}
            </div>
            <details className="field-details"><summary>{pick({ de: "Einheitenprofil", en: "Unit profile" })}</summary><p className="dim" style={{ fontSize: 13, margin: "8px 0" }}>
              {pick(def.bio)}
            </p></details>
          </div>
        </div>

        <div className="unit-slots" role="group" aria-label={t("gear.slots")}>
          {EQUIPMENT_SLOTS.map((value) => {
            const equipped = unit.modules.find((m) => m.slot === value);
            return (
              <button key={value} type="button" className={`unit-slot ${equipped ? "filled" : "empty"}`} aria-pressed={section === "equipment" && slot === value} data-filled={equipped ? "true" : "false"} onClick={() => openSlot(value)} data-testid={`unit-slot-${value}`}>
                <ModuleArtwork slot={value} {...(equipped ? { module: equipped } : {})} size={48} />
                <strong>{t(`module.slot.${value}`)}</strong>
                <small>{equipped ? `+${equipped.enhance}` : t("gear.empty")}</small>
              </button>
            );
          })}
        </div>

        {section === "equipment" ? <UnitEquipment key={`${unit.id}:${slot}`} unit={unit} initialSlot={slot} /> : null}

        <div hidden={section !== "overview"}>
          <div className="stats unit-stats">
            <Stat label="HP" value={def.role === "commander" ? t("units.commanderHp") : formatCompact(unit.stats.hp)} />
            <Stat label="ATK" value={formatCompact(unit.stats.atk)} />
            <Stat label="DEF" value={formatCompact(unit.stats.def)} />
            <Stat label={t("units.stat.interval")} value={`${(unit.stats.intervalTicks / 10).toFixed(1)}s`} />
            <Stat label="CRIT" value={`${Math.round(unit.stats.crit * 100)}% · ×${unit.stats.critDmg.toFixed(2)}`} />
            <Stat label="ACC / EVA" value={`${unit.stats.acc} / ${unit.stats.eva}`} />
            <Stat label={t("units.stat.energy")} value={`+${unit.stats.energyPerAttack}`} />
            <Stat label={t("units.stat.cargo")} value={formatCompact(unit.stats.cargo)} />
          </div>
          <details className="field-details"><summary>{pick({ de: "Was bewirken die Werte?", en: "What do the stats do?" })}</summary><p>{pick({ de: "ATK bestimmt den Schaden pro Treffer; DEF verringert ihn. Das Intervall bestimmt, wie oft die Einheit handelt. CRIT ist Chance und Schadensfaktor. ACC 100 gegen EVA 0 trifft immer.", en: "ATK sets the damage per hit; DEF reduces it. The interval sets how often the unit acts. CRIT is chance and damage factor. ACC 100 against EVA 0 always hits." })}</p></details>

          <UnitXpPanel key={unit.id} unit={unit} hq={roster.caps.hqLevel} />

          {unit.nextStar ? (
            <div className="section" data-testid="unit-star">
              <h3>{t("units.star.next", { n: unit.nextStar.star })}</h3>
              <div className="progress">
                <div className="progress-fill gold" style={{ width: `${Math.min(100, (unit.nextStar.have / unit.nextStar.cost) * 100)}%` }} />
              </div>
              <div className="row" style={{ gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                <span className="mono">
                  FRG {unit.nextStar.have} / {unit.nextStar.cost}
                </span>
                <span className="mono dim">
                  UFR-{def.tier} {unit.nextStar.ufr}
                </span>
              </div>
              <p className="hint">{t("units.star.hint")}</p>
            </div>
          ) : null}

          <div className="section" data-testid="unit-abilities">
            <h3>
              {t("units.abilities")} · CDC {cdc}
            </h3>
            {unit.abilities.map((ab) => (
              <div key={ab.index} className="row list-row" style={{ alignItems: "flex-start" }}>
                <span className="badge tier">
                  {ab.kind === "standard" ? "STD" : ab.kind === "ultimate" ? "ULT" : ab.kind.toUpperCase().slice(0, 3)}
                </span>
                <span className="grow">
                  <strong>{pick(ab.name)}</strong>{" "}
                  <span className="mono dim">
                    Lv {ab.level}/{ab.cap}
                  </span>
                  <div className="hint">{abilityText(ab.archetype, ab.params)}</div>
                </span>
                {ab.nextCost !== null && ab.level < ab.cap ? (
                  <Button
                    style={{ minHeight: 32 }}
                    disabled={cdc < ab.nextCost}
                    onClick={() => void run(() => unitsApi.ability(unit.id, ab.index), t("units.toast.ability"))}
                    data-testid={`ability-up-${ab.index}`}
                  >
                    +1 · {ab.nextCost} CDC
                  </Button>
                ) : (
                  <span className="hint">{ab.level >= 10 ? t("base.reason.max") : t("units.abilityCap", { n: roster.caps.hangarLevel })}</span>
                )}
              </div>
            ))}
          </div>

          <p className="hint">{t("units.roleHint." + (def.role as Role))}</p>
        </div>
      </div>
      {ceremony && ceremony.id === unit.id ? <StarCeremony unit={ceremony} onClose={() => setCeremony(null)} /> : null}
    </Sheet>
  );
}

export type { UnitView };
